import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Calculator, ShieldCheck, Users, Handshake, Sparkles } from "lucide-react";

const STEPS = [
  { icon: Phone, title: "Premier échange & écoute", text: "Nous prenons le temps de comprendre votre situation, vos motivations et votre projet de cession en toute confidentialité." },
  { icon: Calculator, title: "Évaluation & valorisation", text: "Analyse approfondie de votre entreprise : chiffres, rentabilité, actifs et potentiel pour une valorisation juste et objective." },
  { icon: ShieldCheck, title: "Préparation & diffusion confidentielle", text: "Constitution d'un dossier de présentation attractif, puis mise en marché anonymisée auprès de repreneurs ciblés." },
  { icon: Users, title: "Sélection des repreneurs qualifiés", text: "Nous filtrons les candidats sur leur sérieux et leur capacité financière, et orchestrons les premières rencontres." },
  { icon: Handshake, title: "Négociation & accompagnement juridique", text: "Pilotage des négociations, audit, montage financier et sécurisation juridique jusqu'à la signature." },
  { icon: Sparkles, title: "Transmission & suivi post-cession", text: "Accompagnement de la transition pour assurer la pérennité de l'entreprise, de ses équipes et de vos clients." },
];

export default function Timeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.55"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="bg-[#FDFDFC] overflow-x-hidden">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Notre méthode</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Les étapes de votre accompagnement</h2>
          <p className="mt-4 text-[#475569]">De la première prise de contact à la transmission définitive, un parcours structuré et sur-mesure.</p>
        </div>

        <div ref={ref} className="relative mt-16 pl-2">
          {/* Track */}
          <div className="absolute left-[27px] top-2 bottom-2 w-[2px] bg-gray-200" />
          {/* Animated progress */}
          <motion.div style={{ scaleY: lineScale }} className="absolute left-[27px] top-2 bottom-2 w-[2px] bg-[#E0C173] origin-top" />

          <div className="space-y-10">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                data-testid={`timeline-step-${i}`}
                initial={{ opacity: 0, x: 28 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="relative flex items-start gap-4 sm:gap-6"
              >
                <div className="relative z-10 shrink-0">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ type: "spring", stiffness: 220, damping: 16, delay: 0.1 }}
                    className="w-14 h-14 rounded-full bg-[#1B2A4A] flex items-center justify-center shadow-lg ring-4 ring-[#FDFDFC]"
                  >
                    <s.icon className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} />
                  </motion.div>
                </div>
                <div className="flex-1 min-w-0 break-words bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-[#E0C173]">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-display text-lg sm:text-xl text-[#1B2A4A]">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
